import React, { FC, useEffect, useState } from "react";
import {
  FormQuestionDto,
  FormSectionDto,
  QuestionComponentType,
} from "@green0meter/api-client";
import { useFormContext } from "react-hook-form";

import RemoveRowButton from "../RemoveRowButton";
import AddRowButton from "../AddRowButton";
import NumberInput from "../../../NumberInput";
import Select from "../../../Select";
import ShortText from "../../../ShortText";
import DatePicker from "../../../DatePicker";
import Email from "../../../Email";
import RichTextBox from "../../../RichTextBox";
import Files from "../../../Files";

import { useGetFormDraftData } from "@api";
import {
  Box,
  TableBody as MuiTableBody,
  TableCell,
  TableRow,
} from "@components/base";
import { useUserStore } from "@store/user";
import { useDraftReturn } from "@hooks/useDraftField";

interface TableBody {
  formId: string;
  section: FormSectionDto;
  question: FormQuestionDto;
  tableRef: React.RefObject<HTMLTableSectionElement>;
  disabled?: boolean;
  draftField?: useDraftReturn | null;
}

const TableBody: FC<TableBody> = ({
  formId,
  section,
  question,
  disabled,
  tableRef,
  draftField,
}) => {
  const [rowIndexes, setRowIndexes] = useState<number[]>([]);
  const [colMaxWidth, setColMaxWidth] = useState<number>(0);
  const { user } = useUserStore();
  const { formDraftData, formDraftDataEnabled } = useGetFormDraftData(user, formId);

  const { setValue } = useFormContext();

  const handleMaxWidth = (): void => {
    if (tableRef.current) {
      const cellWidth =
        tableRef.current.offsetWidth / (question.childQuestions?.length || 1);

      setColMaxWidth(cellWidth);
    }
  };

  useEffect(() => {
    if (formDraftData && formDraftDataEnabled && question.childQuestions) {
      const childQuestionCodes = question.childQuestions.map((q) => q.code);

      const tableDrafts = formDraftData.filter(
        (draftData) =>
          draftData.parentQuestionCode === question.code &&
          childQuestionCodes.includes(draftData.questionCode),
      );

      const newRowIndexes = Array.from(
        new Set(
          tableDrafts
            .map((d) => d.rowIndex)
            .filter((idx): idx is number => idx !== null),
        ),
      ).sort((a, b) => a - b);

      setRowIndexes(newRowIndexes);

      tableDrafts.forEach((d) => {
        if (d.rowIndex !== null) {
          const fieldName = `${d.questionCode}-${d.rowIndex}`;

          setValue(fieldName, d.value ?? "");
        }
      });
    }
  }, [
    formDraftData,
    formDraftDataEnabled,
    question.childQuestions,
    question.code,
    setValue,
  ]);

  useEffect(() => {
    handleMaxWidth();
    window.addEventListener("resize", handleMaxWidth);

    return () => {
      window.removeEventListener("resize", handleMaxWidth);
    };
  }, []);

  return (
    <MuiTableBody>
      {rowIndexes.map((rowIndex) => (
        <TableRow key={`${question.questionId}-${rowIndex}`}>
          {question.childQuestions &&
            question.childQuestions.map((childQuestion) => {
              const questionEntity = {
                ...childQuestion,
                code: `${childQuestion.code}-${rowIndex}`,
              };

              return (
                <TableCell
                  key={`${question.code}-${childQuestion.questionId}-${rowIndex}`}
                  width={colMaxWidth}
                >
                  <Box maxWidth={`${colMaxWidth}px`} minWidth={100}>
                    {childQuestion.componentType ===
                      QuestionComponentType.Number && (
                      <NumberInput
                        parentQuestionCode={question.code}
                        question={questionEntity}
                        disabled={disabled}
                        draftField={draftField}
                      />
                    )}
                    {childQuestion.componentType ===
                      QuestionComponentType.ShortText && (
                      <ShortText
                        parentQuestionCode={question.code}
                        question={questionEntity}
                        disabled={disabled}
                        draftField={draftField}
                      />
                    )}
                    {childQuestion.componentType ===
                      QuestionComponentType.RichTextBox && (
                      <RichTextBox
                        parentQuestionCode={question.code}
                        question={questionEntity}
                        disabled={disabled}
                        draftField={draftField}
                      />
                    )}
                    {childQuestion.componentType === QuestionComponentType.Files && (
                      <Files
                        parentQuestionCode={question.code}
                        question={questionEntity}
                        originalQuestionCode={childQuestion.code}
                        disabled={disabled}
                        rowIndex={rowIndex}
                        formId={formId}
                      />
                    )}
                    {childQuestion.componentType ===
                      QuestionComponentType.DatePicker && (
                      <DatePicker
                        question={questionEntity}
                        disabled={disabled}
                        parentQuestionCode={question.code}
                      />
                    )}
                    {childQuestion.componentType === QuestionComponentType.Email && (
                      <Email
                        parentQuestionCode={question.code}
                        question={questionEntity}
                        disabled={disabled}
                        draftField={draftField}
                      />
                    )}
                    {childQuestion.componentType ===
                      QuestionComponentType.Select && (
                      <Select
                        question={questionEntity}
                        disabled={disabled}
                        parentQuestionCode={question.code}
                        rowIndex={rowIndex}
                      />
                    )}
                  </Box>
                </TableCell>
              );
            })}
          <TableCell>
            <RemoveRowButton
              formId={formId}
              sectionCode={section.code}
              questionCode={question.code}
              rowIndex={rowIndex}
            />
          </TableCell>
        </TableRow>
      ))}
      {question.childQuestions && (
        <TableRow>
          <TableCell colSpan={question.childQuestions.length + 2}>
            <Box py={2}>
              <AddRowButton
                formId={formId}
                section={section}
                question={question}
                disabled={disabled}
              />
            </Box>
          </TableCell>
        </TableRow>
      )}
    </MuiTableBody>
  );
};

export default TableBody;
